import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, Search, Calculator, Beaker, Activity, DollarSign, TrendingUp, Brain } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    // Log 404 errors for monitoring
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    
    // Optional: Send to analytics if you have it set up
    // This helps track broken links
  }, [location.pathname]);

  const popularCategories = [
    {
      name: "Physics",
      path: "/physics",
      icon: Activity,
      description: "Mechanics, energy, and motion calculators"
    },
    {
      name: "Chemistry",
      path: "/chemistry",
      icon: Beaker,
      description: "Molecular, reaction, and solution calculators"
    },
    {
      name: "Math",
      path: "/math",
      icon: Calculator,
      description: "Algebra, geometry, and statistics tools"
    },
    {
      name: "Finance",
      path: "/finance",
      icon: DollarSign,
      description: "Investment, loan, and business calculators"
    },
    {
      name: "Health",
      path: "/health",
      icon: TrendingUp,
      description: "BMI, BMR, and fitness calculators"
    },
    {
      name: "SuperCalc AI",
      path: "/supercalc",
      icon: Brain,
      description: "AI-powered complex equation solver"
    }
  ];

  return (
    <PageLayout>
      <SEO 
        title="404 - Page Not Found | CalcLab"
        description="The page you're looking for doesn't exist. Explore our 100+ free calculators for physics, chemistry, math, finance, and more."
        canonical={`https://calclab.app${location.pathname}`}
      />
      
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Error Message */}
        <div className="text-center mb-12">
          <h1 className="text-8xl font-bold text-primary mb-4">404</h1>
          <h2 className="text-3xl font-semibold mb-4">Page Not Found</h2>
          <p className="text-lg text-muted-foreground mb-2">
            Sorry, the page <code className="bg-muted px-2 py-1 rounded">{location.pathname}</code> doesn't exist.
          </p>
          <p className="text-muted-foreground mb-8">
            It may have been moved, deleted, or the URL might be incorrect.
          </p>
          
          {/* Primary Actions */}
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <Button asChild size="lg">
              <Link to="/">
                <Home className="mr-2 h-5 w-5" />
                Go to Homepage
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/search">
                <Search className="mr-2 h-5 w-5" />
                Search Calculators
              </Link>
            </Button>
          </div>
        </div>

        {/* Popular Categories */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-center mb-6">Explore Popular Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularCategories.map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.path} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                    </div>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="ghost" className="w-full">
                      <Link to={category.path}>
                        Browse {category.name}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Additional Help */}
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
            <CardDescription>
              If you believe this page should exist or you're experiencing issues, here are some helpful resources:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li>
                <Link to="/sitemap" className="text-primary hover:underline">
                  View our complete sitemap
                </Link>
              </li>
              <li>
                <Link to="/tools" className="text-primary hover:underline">
                  Browse all calculator tools
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-primary hover:underline">
                  Contact support
                </Link>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default NotFound;
